import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Grid } from '../../app/StyleGuide';
import AnnualSubscriptionModal, { AnnualUpgradeActionWrapper } from '../../components/AnnualSubscriptionModal';
import { SmallRoundedIconButton } from '../../components/Buttons';
import Dropdown, { DropdownItem } from '../../components/Dropdown';
import useModal from '../../hooks/useModal';
import alertService from '../../services/AlertService';
import licenseService from '../../services/licenseService';
import windowService from '../../services/windowService';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import { LicenseWithCurriculum } from './hooks/useFetchSubscriptions';
import LicenseDetails from './LicenseDetails';
import { License } from './models/license';
import ResumeSubscriptionModal from './ResumeSubscriptionModal';

const LicenseContainer = styled.div`
  padding: ${Grid._4};
  width: 100%;
  &:not([data-disabled]) {
    cursor: pointer;

    &:hover {
      h4 {
        text-decoration: underline;
      }
    }
  }

  grid-template-columns: 1fr auto;
`;

const LicenseListItem = ({
  license: initialLicense,
  onClick,
  hasUpcomingSubscription,
}: {
  license: LicenseWithCurriculum;
  onClick?: () => void;
  hasUpcomingSubscription?: boolean;
}) => {
  const [license, setLicense] = useState<LicenseWithCurriculum>(initialLicense);

  const updateSubscriptionStatus = useCallback((status: string) => {
    if (!status) return;

    setLicense((prev: License) =>
      prev.subscription ? { ...prev, subscription: { ...prev.subscription, status } } : prev
    );
  }, []);

  const [modal, openModal] = useModal((type: string, payload: unknown, dismissModal: () => void) => {
    switch (type) {
      case 'cancel':
        return (
          <CancelSubscriptionModal
            license={license}
            updateSubscriptionStatus={updateSubscriptionStatus}
            handleDismiss={dismissModal}
          />
        );
      case 'resume':
        return (
          <ResumeSubscriptionModal
            license={license}
            updateSubscriptionStatus={updateSubscriptionStatus}
            handleDismiss={dismissModal}
          />
        );
      case 'annual':
        return (
          <AnnualSubscriptionModal
            subscription={payload as NonNullable<License['subscription']>}
            onUpgradeComplete={handleUpgradedSubscription}
            handleDismiss={dismissModal}
          />
        );
      default:
        return null;
    }
  });

  const canManage = !license.expired && !licenseService.isIndividualLicense(license);
  const canEdit = !license.expired && licenseService.canUpgradeDowngrade(license);
  const canCancel = !license.expired && licenseService.isActiveSubscription(license);
  const canResume = !license.expired && licenseService.isInactiveSubscription(license);
  const canResubscribe = license.expired && licenseService.hasSubscription(license);
  const canSwitchToAnnual =
    !license.expired &&
    licenseService.isActiveSubscription(license) &&
    licenseService.isMonthlySubscription(license) &&
    licenseService.isCurriculumLicense(license);

  const hasActionableButtons = canEdit || canCancel || canResume || canResubscribe || canSwitchToAnnual;

  const redirectToManageLicense = () => windowService.redirectTo(`/org/purchases-and-subscriptions/${license.id}`);
  const handleClick = () => {
    if (!canManage) return;
    if (onClick) return onClick();
    return redirectToManageLicense();
  };

  function handleUpgradedSubscription(newSubscription: Partial<NonNullable<License['subscription']>>) {
    setLicense((prev: License) => {
      if (!prev.subscription) return prev;
      return {
        ...prev,
        subscription: { ...prev.subscription, ...newSubscription },
      };
    });
    alertService.show('Subscription Updated');
  }

  const showModal = (e: React.MouseEvent, ...args: unknown[]) => {
    e.stopPropagation();
    openModal(...args);
  };

  const openMyLifewaySubscriptions = (e: React.MouseEvent) => {
    e.stopPropagation();
    windowService.openUrl('https://my.lifeway.com/subscriptions');
  };

  const buttonActions = [
    {
      condition: canEdit,
      iconClass: 'fas fa-pen',
      buttonText: 'Edit Subscription',
      onClick: () => {},
    },
    ...(window.featureToggles.get('SWITCH_CURRICULUM_SUBSCRIPTION') === true
      ? [
          {
            condition: canCancel && !license.isGridSubscription,
            iconClass: 'fas fa-exchange-alt',
            buttonText: 'Switch Subscription',
            onClick: () =>
              windowService.redirectTo(`/org/purchases-and-subscriptions/${license.subscription?.id}/switch`),
          },
        ]
      : []),
    {
      condition: canCancel,
      iconClass: 'fas fa-ban',
      buttonText: 'Cancel Subscription',
      onClick: (event: React.MouseEvent) => showModal(event, 'cancel'),
    },
    {
      condition: canResume,
      iconClass: 'fas fa-credit-card',
      buttonText: 'Resume Subscription',
      onClick: (event: React.MouseEvent) => showModal(event, 'resume'),
    },
    {
      condition: canResubscribe,
      iconClass: 'fas fa-credit-card',
      buttonText: 'Resubscribe',
      onClick: openMyLifewaySubscriptions,
    },
  ];

  return (
    <>
      <LicenseContainer
        data-qa-hook="subscriptionItem"
        className="grid-container grid-gap-24"
        data-disabled={!canManage ? 'true' : undefined}
      >
        <div onClick={handleClick}>
          <LicenseDetails license={license} showAdditionalDetails hasUpcomingSubscription={hasUpcomingSubscription} />
        </div>
        {(canManage || hasActionableButtons) && !licenseService.isFutureSubscription(license) && (
          <div className="grid-container grid-gap-16">
            <Dropdown
              placement="left"
              trigger={
                <SmallRoundedIconButton aria-label="License actions">
                  <i className="fas fa-ellipsis-v" />
                </SmallRoundedIconButton>
              }
            >
              <>
                {canManage && (
                  <DropdownItem onClick={handleClick}>
                    <i className="fas fa-info-circle" /> View Details
                  </DropdownItem>
                )}
                {canSwitchToAnnual && (
                  <AnnualUpgradeActionWrapper subscription={license.subscription}>
                    {annualSubscription => (
                      <DropdownItem onClick={e => showModal(e, 'annual', annualSubscription)}>
                        <i className="fas fa-cog" />
                        Switch to Annual
                      </DropdownItem>
                    )}
                  </AnnualUpgradeActionWrapper>
                )}
                {buttonActions
                  .filter(action => action.condition)
                  .map(action => (
                    <DropdownItem key={action.buttonText} onClick={e => action.onClick(e)}>
                      <i className={action.iconClass} /> {action.buttonText}
                    </DropdownItem>
                  ))}
              </>
            </Dropdown>
          </div>
        )}
      </LicenseContainer>
      {modal}
    </>
  );
};

export default LicenseListItem;
